// import data from "../../config/hierarchySearch.json"

import { createGradeRegex } from "components/coloredMaterialDetails/Industry";
import type { FilterEntry } from "hooks/filters";
import type { Hierachy } from "hooks/useHierarchyData";

type Application = {
  application: string;
  grade: string[];
};

type ApplicationClass = {
  class: string;
  application: Application[];
};

export type Result = {
  grade: string;
  class: string[];
  industry: string[];
  application: string[];
};

export const validationHierachyData = (data: unknown) => {
  if (!data || !Array.isArray(data) || (data && !data.length)) {
    return [];
  }
  return data;
};

export const getIndustrySelect = (data: Hierachy[]) => {
  data = validationHierachyData(data);
  return data.map((industry) => {
    return {
      name: industry.industry,
      value: industry.industry,
    };
  });
};

// not use yet
export const getApplicationClassSelect = (data: ApplicationClass[]) => {
  return data.map((app) => {
    return {
      name: app.class,
      value: app.class,
    };
  });
};

export const getApplicationByClass = (
  industry: string,
  applicationClass: string,
  data: Hierachy[],
) => {
  data = validationHierachyData(data);
  const industryObj = data.find((item) => item.industry === industry);
  if (industryObj) {
    const classObj = industryObj.applicationClass.find(
      (item: any) => item.class === applicationClass,
    );
    if (classObj) {
      return classObj.application;
    } else {
      return [];
    }
  } else {
    return [];
  }
};

const getCurrentFilterName = (name?: string, filters?: Array<FilterEntry>) => {
  if (!name || !filters) return "";
  const filter = filters?.find((f) => f.type === name);
  return filter?.value[0].name;
};

export const getFilterName = (
  industry: string,
  appClass?: string,
  app?: string,
  filters?: Array<FilterEntry>,
) => {
  if (!filters?.find((f) => f.type === industry)) return undefined;

  return {
    industryName: getCurrentFilterName(industry, filters),
    applicationClassName: getCurrentFilterName(appClass, filters),
    applicationName: getCurrentFilterName(app, filters),
  };
};

// not use yet
export const getGradesByIndustryClassApplication = (
  industry: string,
  className: string,
  applicationName: string,
  data: Hierachy[],
) => {
  const industryObj = data.find((item) => item.industry === industry);

  if (industryObj) {
    const classObj = industryObj.applicationClass.find(
      (item) => item.class === className,
    );

    if (classObj) {
      const applicationObj = classObj.application.find(
        (item) => item.application === applicationName,
      );

      if (applicationObj) {
        return applicationObj.grade;
      } else {
        return [];
      }
    } else {
      return [];
    }
  } else {
    return [];
  }
};

export const getClassesByIndustry = (data: Hierachy[], industry?: string) => {
  data = validationHierachyData(data);
  const industryObj = data.find((item) => item.industry === industry);
  if (industryObj) {
    return industryObj.applicationClass.map((item) => ({
      name: item.class,
      value: item.class,
    }));
  } else {
    return [];
  }
};

// not use yet
export const getGradesByConditions = (
  data: Hierachy[],
  industry?: string,
  className?: string,
  applicationName?: string,
) => {
  // Find the industry object that matches the input industry
  const industryObj = data.find((item) => item.industry === industry);

  if (!industryObj || !industry) {
    return getAllUniqueGrades(data);
  }

  // If only industry is provided, get all grades from all classes and applications
  if (!className) {
    const grades: string[] = [];
    industryObj.applicationClass.forEach((appClass) => {
      appClass.application.forEach((app) => {
        grades.push(...app.grade);
      });
    });
    return grades.length > 0 ? Array.from(new Set(grades)) : [];
  }

  // If industry and class are provided, get all grades from the specified class and its applications
  const classObj = industryObj.applicationClass.find(
    (item) => item.class === className,
  );

  if (!classObj) {
    return [];
  }

  // If only industry and class are provided, gather grades from all applications in the class
  if (!applicationName) {
    const grades: string[] = [];
    classObj.application.forEach((app) => {
      grades.push(...app.grade);
    });
    return grades.length > 0 ? Array.from(new Set(grades)) : [];
  }

  // If industry, class, and application are provided, find the specific application and return its grades
  const applicationObj = classObj.application.find(
    (item) => item.application === applicationName,
  );

  if (!applicationObj) {
    return [];
  }

  // Return the grades for that specific application
  return applicationObj.grade.length > 0
    ? Array.from(new Set(applicationObj.grade))
    : [];
};

export const transformMaterialData = (data: Hierachy[]): Result[] => {
  const resultMap: { [key: string]: Result } = {}; // Map to track unique entries by grade
  data = validationHierachyData(data);
  // Iterate through the data
  data.forEach((industryObj) => {
    industryObj.applicationClass.forEach((appClass) => {
      appClass.application.forEach((app) => {
        (app.grade || []).forEach((grade) => {
          // If the grade is not yet in the map, create a new entry
          if (!resultMap[grade]) {
            resultMap[grade] = {
              grade: grade,
              class: [],
              industry: [],
              application: [],
            };
          }

          // Get the corresponding entry for the grade
          const gradeEntry = resultMap[grade];

          // Add class, industry, and application, ensuring no duplicates
          if (!gradeEntry.class.includes(appClass.class)) {
            gradeEntry.class.push(appClass.class);
          }
          if (!gradeEntry.industry.includes(industryObj.industry)) {
            gradeEntry.industry.push(industryObj.industry);
          }
          if (!gradeEntry.application.includes(app.application)) {
            gradeEntry.application.push(app.application);
          }
        });
      });
    });
  });

  // Convert the result map to an array
  return Object.values(resultMap);
};

export const getAllUniqueGrades = (data: Hierachy[]): string[] => {
  let grades: any[] = [];
  data.forEach((industry) => {
    industry.applicationClass.forEach((appClass) => {
      appClass.application.forEach((app) => {
        grades = grades.concat(app.grade);
      });
    });
  });

  return Array.from(new Set<string>(grades));
};

// not use yet
export const extractAllApplicationClass = (
  data: Hierachy[],
): ApplicationClass[] => {
  // To track unique application classes
  const seenClasses: Map<string, ApplicationClass> = new Map();

  // Iterate through industries and application classes
  for (const industry of data) {
    for (const appClass of industry.applicationClass) {
      // If the class has already been added, merge the application arrays
      if (seenClasses.has(appClass.class)) {
        const existingClass = seenClasses.get(appClass.class);
        // Set for fast lookup
        const existingAppSet = new Set(
          existingClass?.application.map((app) => app.application),
        );

        // Merge applications and avoid duplicates
        for (const app of appClass.application) {
          if (!existingAppSet.has(app.application)) {
            existingClass?.application.push(app);
            // Add to the set to avoid future duplicates
            existingAppSet.add(app.application);
          }
        }
      } else {
        // If the class is not yet added, simply add it with a copy of the applications
        seenClasses.set(appClass.class, {
          class: appClass.class,
          application: [...appClass.application],
        });
      }
    }
  }

  // Convert Map values to an array to get the final list of merged unique application classes
  return Array.from(seenClasses.values());
};

interface GradeProps {
  hierachyData: Hierachy[];
  material: { material: { shortGrade?: string } };
  activeIndustry?: string;
  activeApplicationClass?: string;
  activeApplication?: string;
}

export const gradeValue = ({
  hierachyData,
  material,
  activeIndustry,
  activeApplicationClass,
  activeApplication,
}: GradeProps): Result | undefined => {
  const scopedData = scopeHierarchyData(
    hierachyData,
    activeIndustry,
    activeApplicationClass,
    activeApplication,
  );
  const gradeList = transformMaterialData(scopedData);
  return gradeList?.find((grade) => {
    if (material.material.shortGrade) {
      return createGradeRegex(grade.grade).test(material.material.shortGrade);
    }
    return false;
  });
};

function scopeHierarchyData(
  data: Hierachy[],
  activeIndustry?: string,
  activeApplicationClass?: string,
  activeApplication?: string,
): Hierachy[] {
  if (!activeIndustry) return data;

  const industry = data.find((i) => i.industry === activeIndustry);
  // Unknown activeIndustry (stale URL param or hierarchy not yet loaded) →
  // return empty so gradeValue produces undefined rather than matching across
  // a completely unrelated industry and showing wrong restriction labels.
  if (!industry) return [];

  let applicationClasses = industry.applicationClass;

  if (activeApplicationClass) {
    applicationClasses = applicationClasses.filter(
      (ac) => ac.class === activeApplicationClass,
    );
  }

  if (activeApplication) {
    applicationClasses = applicationClasses.map((ac) => ({
      ...ac,
      application: ac.application.filter(
        (a) => a.application === activeApplication,
      ),
    }));
  }

  return [{ ...industry, applicationClass: applicationClasses }];
}

export const InternalApplication = [
  "Functional Film",
  "Wetside",
  "Sheet",
  "Compounding",
];
export const RestrictedApplication = ["Restricted"];
export const TrialApplication = ["Trail Product"];
export const IndustryInternal = ["Internal"];
